import { useCallback, useEffect, useRef, useState } from 'react'

import type { RegionLatency } from './measure'

/*
 * When the latency measurement runs, and when it deliberately does not.
 *
 * §10.3: "One automatic run when the section becomes visible, plus an explicit
 * **Re-measure** control. No polling, no background loop, no probe while the tab
 * is hidden."
 *
 * Three restraints, each with a reason worth keeping:
 *
 *   **when visible**   a reader who never scrolls to the regions never sends a
 *                      request to four hostnames on their behalf
 *   **not hidden**     a backgrounded tab's timers are throttled, so a probe
 *                      running there would measure the browser's scheduler
 *                      rather than the network — and report the result as
 *                      network latency
 *   **no polling**     the page already polls the control plane every 30 s;
 *                      adding twenty cross-region requests to that cadence
 *                      would make an idle open tab a traffic source
 *
 * ── The module is imported dynamically, and that is a budget decision ──────
 *
 * §8 budgets this app at 75 kB of initial JS and S4 measured 68.2 kB of it
 * spent. The measurement code cannot run before first paint by construction —
 * it waits for an intersection — so paying for it in the entry chunk would be
 * paying for something that is definitionally not needed yet. `import()` inside
 * the effect, rather than `lazy()` + `Suspense`: nothing here renders, so a
 * suspense boundary would be ceremony around a function call.
 */

export type LatencyState =
  /** Not started. The section has not been looked at. */
  | { readonly kind: 'idle' }
  | { readonly kind: 'measuring' }
  | {
      readonly kind: 'done'
      readonly results: readonly RegionLatency[]
      /**
       * §10.4's "Fastest for you", or `null` when no lead is clear enough.
       *
       * Carried in the state rather than computed by the renderer, and that is
       * a bundle decision rather than a style one: a renderer that called
       * `fastest()` would need a **static** import of `./measure`, which would
       * pull the whole probe module into the entry chunk and undo the dynamic
       * import twenty lines below. Computed here, where the module is already
       * loaded, it costs nothing.
       */
      readonly fastestCode: string | null
    }
  /** The module itself could not be loaded — a chunk fetch that failed. Not the
   *  same as every region failing to answer, which is a `done` with nulls. */
  | { readonly kind: 'unavailable' }

export interface Latency {
  readonly state: LatencyState
  /** Attach to the element whose visibility starts the run. */
  readonly ref: (node: HTMLElement | null) => void
  readonly remeasure: () => void
}

export function useLatency(codes: readonly string[]): Latency {
  const [state, setState] = useState<LatencyState>({ kind: 'idle' })
  const running = useRef<AbortController | null>(null)
  const element = useRef<HTMLElement | null>(null)
  /* A run that was wanted and refused because the tab was hidden. See the
     `visibilitychange` effect below. */
  const deferred = useRef(false)
  /* The codes as a stable string, so the effect below does not restart on every
     render because the parent rebuilt the array. */
  const key = codes.join(',')

  const measure = useCallback(() => {
    if (key === '') return
    if (document.visibilityState !== 'visible') {
      /*
       * Refused, and **remembered**.
       *
       * §10.3 forbids probing a hidden tab, and the first version stopped
       * there — which left a real gap: a page opened in a background tab has
       * its regions section intersect immediately, the observer fires once,
       * disconnects, and the run is refused. Switching to the tab afterwards
       * re-triggered nothing, so the section sat blank for as long as it was
       * open. Found in a browser, where `document.visibilityState` was
       * `hidden` for a page that was plainly on screen.
       */
      deferred.current = true
      return
    }
    deferred.current = false

    running.current?.abort()
    const controller = new AbortController()
    running.current = controller
    setState({ kind: 'measuring' })

    void import('./measure')
      .then(async ({ measureRegions, fastest }) => {
        const template = import.meta.env.VITE_PROBE_URL_TEMPLATE
        const results = await measureRegions(key.split(','), {
          signal: controller.signal,
          ...(template === undefined || template === '' ? {} : { urlTemplate: template }),
        })
        if (!controller.signal.aborted) {
          setState({ kind: 'done', results, fastestCode: fastest(results) })
        }
      })
      .catch(() => {
        /* A failed chunk fetch, or an unexpected throw. Reported as its own
           state rather than as "every region is unreachable": the second would
           be a claim about the network, and this is a claim about this page. */
        if (!controller.signal.aborted) setState({ kind: 'unavailable' })
      })
  }, [key])

  /* One automatic run, when the section is first looked at. */
  useEffect(() => {
    const node = element.current
    if (node === null || key === '') return undefined

    let started = false
    const observer = new IntersectionObserver((entries) => {
      if (started) return
      if (!entries.some((entry) => entry.isIntersecting)) return
      started = true
      observer.disconnect()
      measure()
    })
    observer.observe(node)
    return () => {
      observer.disconnect()
    }
  }, [key, measure])

  /* The deferred run, taken up when the tab is looked at. */
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible' && deferred.current) measure()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [measure])

  useEffect(
    () => () => {
      running.current?.abort()
    },
    [],
  )

  const ref = useCallback((node: HTMLElement | null) => {
    element.current = node
  }, [])

  return { state, ref, remeasure: measure }
}
